import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * OverdueOrdersTab — every GPO past its delivery date and still active.
 *
 * READ LIVE ON EVERY MOUNT AND ON DEMAND. There is no cached count and no snapshot: the whole
 * value of this list is that it cannot be stale. An order that ships this morning must be gone
 * from it this afternoon. The "as of" stamp is shown so a merchant knows exactly how fresh what
 * they are looking at is, rather than assuming.
 *
 * SORTED WORST-FIRST, server-side. GPO number is an accident of when a document was raised and
 * tells a merchant nothing about what to chase.
 *
 * THE INVOICE COLUMN IS THE POINT OF THE SCREEN, not decoration. ZERO M23 Ex-Factory milestones
 * are recorded across this population, yet several of these orders are fully invoiced. An order
 * invoiced at 100% is finished and should stop being chased; one invoiced at 3% after two months
 * is a stalled shipment or a missing invoice. The ERP cannot tell which — it has never had a
 * dispatch or a packing list — so the row states the facts and names the ambiguity instead of
 * resolving it.
 */
export default function OverdueOrdersTab() {
  const [rows, setRows] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [buyer, setBuyer] = useState('');
  const [state, setState] = useState('');

  const load = useCallback(() => {
    setBusy(true); setErr('');
    api.get('/oe/overdue-orders')
      .then(r => { setRows(r.data.data || []); setAsOf(r.data.as_of || null); })
      .catch(e => setErr(e.response?.data?.error || 'Could not load overdue orders'))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

  // Severity by lateness. The bands are the order a merchant works in, not decoration.
  const sev = (d) => (d >= 42 ? { bg: '#FCEBEB', fg: '#A32D2D' } : d >= 21 ? { bg: '#FBF6EC', fg: '#9A6B00' } : { bg: '#F1F5F2', fg: '#166534' });

  const STATE_LABEL = {
    partial: ['partly received', '#9A6B00', '#FBF6EC'],
    nothing_received: ['nothing received', '#A32D2D', '#FCEBEB'],
    all_received: ['all received', '#166534', '#EDF6F0'],
    no_po: ['no PO raised', '#6B7280', '#F3F4F6'],
  };

  const buyers = [...new Set((rows || []).map(r => r.buyer).filter(Boolean))].sort();
  const shown = (rows || []).filter(r => (!buyer || r.buyer === buyer) && (!state || r.receipt_state === state));

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap', background: '#F7F6F3' };
  const td = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', verticalAlign: 'middle' };
  const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const sel = { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12.5 };
  const chip = (text, fg, bg, title) => (
    <span title={title} style={{ fontSize: 10, fontWeight: 700, background: bg, color: fg, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>{text}</span>
  );

  if (err) return <div style={{ padding: 20 }}><div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px', borderRadius: 6, fontSize: 13 }}>{err}</div></div>;
  if (!rows) return <div style={{ padding: 24, color: '#aaa', fontSize: 12.5 }}>Loading…</div>;

  const openValue = shown.reduce((s, r) => s + Number(r.open_po_value || 0), 0);
  const orderValue = shown.reduce((s, r) => s + Number(r.order_value || 0), 0);
  const worst = shown.length ? Math.max(...shown.map(r => r.days_overdue)) : 0;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: '#555' }}>
          <strong style={{ fontSize: 20, color: '#A32D2D' }}>{shown.length}</strong> order{shown.length === 1 ? '' : 's'} past delivery date
          {shown.length !== rows.length && <span style={{ color: '#888' }}> (of {rows.length})</span>}
        </div>
        <div style={{ fontSize: 12.5, color: '#666' }}>order value <strong>{inr(orderValue)}</strong></div>
        <div style={{ fontSize: 12.5, color: '#9A6B00' }}>open POs <strong>{inr(openValue)}</strong></div>
        <div style={{ fontSize: 12.5, color: '#A32D2D' }}>worst <strong>{worst} days</strong></div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={sel} value={buyer} onChange={e => setBuyer(e.target.value)}>
            <option value="">All buyers</option>
            {buyers.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select style={sel} value={state} onChange={e => setState(e.target.value)}>
            <option value="">Any receipt state</option>
            {Object.entries(STATE_LABEL).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
          </select>
          <button onClick={load} disabled={busy} style={{ padding: '6px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* The freshness stamp is deliberate: this list is only useful if the reader knows it is
          live, and "live" is a claim that should carry a timestamp. */}
      <div style={{ fontSize: 11, color: '#999', marginBottom: 10 }}>
        Read live from the order book{asOf ? ` · as of ${new Date(asOf).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}. Nothing here is cached.
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {['GPO', 'Buyer', 'Style', 'Due', 'Late', 'Qty', 'Order value', 'Open POs', 'Open value', 'Receipt', 'Invoiced'].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={11} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>
                Nothing overdue on this filter.
              </td></tr>
            )}
            {shown.map(r => {
              const s = sev(r.days_overdue);
              const st = STATE_LABEL[r.receipt_state] || ['—', '#888', '#F3F4F6'];
              return (
                <tr key={r.gpo_no} style={{ borderLeft: `3px solid ${s.fg}` }}>
                  <td style={{ ...td, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{r.gpo_no}</td>
                  <td style={td}>{r.buyer || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal', minWidth: 150, color: '#555' }}>{r.style_no || '—'}</td>
                  <td style={num}>{fmt(r.delivery_date)}</td>
                  <td style={{ ...num, fontWeight: 700, color: s.fg }}>{r.days_overdue}d</td>
                  <td style={num}>{Number(r.order_qty || 0).toLocaleString('en-IN')}</td>
                  <td style={num}>{inr(r.order_value)}</td>
                  <td style={num}>{r.open_po_lines || 0}</td>
                  <td style={{ ...num, color: r.open_po_value > 0 ? '#9A6B00' : '#bbb' }}
                      title={r.partly_received_pos > 0
                        ? `Gross. ${r.partly_received_pos} of these open POs already has material booked against it, so the true outstanding value is lower. Receipt is booked per PO, not per line, so it cannot be netted exactly.`
                        : undefined}>
                    {r.open_po_value > 0 ? inr(r.open_po_value) : '—'}
                    {r.partly_received_pos > 0 && <span style={{ color: '#9A6B00', fontWeight: 700 }}>*</span>}
                  </td>
                  <td style={td}>{chip(st[0], st[1], st[2])}</td>
                  <td style={td}>
                    {r.invoice_count > 0 ? (
                      <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                        {chip(`${r.invoiced_pct}%`, r.invoiced_pct >= 100 ? '#166534' : r.invoiced_pct >= 60 ? '#9A6B00' : '#A32D2D',
                              r.invoiced_pct >= 100 ? '#EDF6F0' : r.invoiced_pct >= 60 ? '#FBF6EC' : '#FCEBEB',
                              `${Number(r.invoiced_qty).toLocaleString('en-IN')} of ${Number(r.order_qty).toLocaleString('en-IN')} invoiced · ${r.invoice_count} invoice(s)${r.busy_nos ? ' · Busy ' + r.busy_nos : ''}`)}
                        {r.ahead_of_milestones && chip('no ex-factory', '#3B3A8F', '#EEEDF8',
                          'Invoiced, but no Ex-Factory (M23) milestone is recorded. Either it shipped without the milestone being marked, or the invoice was transcribed from Busy. Worth checking before chasing this order.')}
                      </span>
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {shown.some(r => r.partly_received_pos > 0) && (
        <div style={{ marginTop: 12, fontSize: 11, color: '#8a6a1f' }}>
          <strong style={{ color: '#9A6B00' }}>*</strong> Open value is gross: some of these POs already have material booked against them but stay open until they close.
          Receipt is recorded per PO rather than per line, so the arrived portion cannot be netted off exactly.
        </div>
      )}

      {rows.some(r => r.ahead_of_milestones) && (
        <div style={{ marginTop: 12, padding: '10px 13px', background: '#EEEDF8', border: '1px solid #d5d3f0', borderRadius: 6, fontSize: 11.5, color: '#2D2C72', lineHeight: 1.6, maxWidth: 900 }}>
          <strong>{rows.filter(r => r.ahead_of_milestones).length} of these are invoiced but carry no Ex-Factory milestone.</strong> No dispatch or packing list has ever been recorded in the system,
          so the ERP cannot say whether they shipped without the milestone being marked or the invoice was transcribed from Busy.
          Read the percentage: at <strong>100%</strong> the order is finished and should stop being chased; well under it, either the shipment stalled or an invoice is missing.
        </div>
      )}
    </div>
  );
}
